import ReactDOM from "react-dom";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronUp, UserCircle, CreditCard } from "lucide-react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useStripe } from "@stripe/react-stripe-js";
import Modal from "components/modal";
import PlanDetails from "sections/modal/plan-details";
import useModal from "utils/hooks/use-modal";
import ShimmerLoader from "components/shimmer-loader";
import ProfileModal from "components/profile-modal";
import { getUserByCogId, logoutCognitoUser } from "api/user";
import { clearOnboardingProgress } from "components/set-up-profile";
import { getSubscriptionData } from "api/subscriptions";
import { getUsageData } from "api/usage";
import { getAllStripeProducts, createCheckoutSession } from "api/stripe";
import { ReactComponent as EnterIcon } from "assets/svgs/enter.svg";
import useComponentClickOutside from "utils/hooks/use-component-click-outside";

const ProfileDropdown = () => {
  const navigate = useNavigate();
  const { isComponentVisible, toggleChild, wrapRef } = useComponentClickOutside();
  const [showProfileModal, setShowProfileModal] = useState(false);

  const { openModal: openPlanModal, closeModal: closePlanModal, isOpen: isPlanOpen } = useModal(false);

  const stripe = useStripe();
  const queryClient = useQueryClient();
  const [planLoading, setPlanLoading] = useState(null);

  const { data, isLoading } = useQuery("userProfile", getUserByCogId);
  const { data: stripeProducts } = useQuery("stripe-products", getAllStripeProducts, { retry: false });
  const { data: subscriptionData } = useQuery("userSubscription", getSubscriptionData, { retry: false });
  const { data: usageData } = useQuery("userUsage", getUsageData, { retry: false });

  const handleSignOut = async () => {
    try {
      const isSignedOut = await logoutCognitoUser();
      if (isSignedOut) {
        // Drop cached profile/onboarding state so the next user signing in on
        // this tab is evaluated by the onboarding gate from scratch.
        clearOnboardingProgress();
        queryClient.clear();
        navigate("/login");
      }
    } catch (error) {
      console.error("Failed signing out:", error);
    }
  };

  const handleCheckout = async (event, priceId) => {
    event.preventDefault();
    setPlanLoading(priceId);
    try {
      const response = await createCheckoutSession({ priceId });
      const result = await stripe.redirectToCheckout({ sessionId: response.id });
      if (result.error) {
        toast.error(result.error.message || "Payment failed", { position: "top-right" });
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || "Error processing payment!", { position: "top-right" });
    } finally {
      setPlanLoading(null);
    }
  };

  const getProfileInitials = () => {
    if (!data) return "";
    const first = data.firstName?.[0]?.toUpperCase() ?? "";
    const last = data.lastName?.[0]?.toUpperCase() ?? "";
    return first + last;
  };

  const getDisplayName = () => {
    if (!data) return "";
    const full = `${data.firstName ?? ""} ${data.lastName ?? ""}`.trim();
    return full.length > 22 ? full.substring(0, 20) + "…" : full;
  };

  return (
    <>
      <div ref={wrapRef} className="relative w-full">
        {/* Trigger button */}
        <button
          type="button"
          onClick={toggleChild}
          className="flex items-center gap-3 w-full cursor-pointer select-none px-3 py-2.5 rounded-xl border border-white/20 hover:bg-white/15 hover:border-white/30 transition-all duration-200 focus:outline-none"
          aria-expanded={isComponentVisible}
          aria-label="Account menu"
        >
          {/* Avatar */}
          <div className="flex-shrink-0 h-9 w-9 bg-white rounded-full flex items-center justify-center ring-2 ring-white/30 shadow-sm">
            {isLoading ? (
              <ShimmerLoader className="h-full w-full rounded-full" />
            ) : (
              <span className="font-bold text-sm text-brand-purple leading-none">
                {getProfileInitials()}
              </span>
            )}
          </div>

          {/* Name */}
          <div className="flex-1 min-w-0 text-left">
            {isLoading ? (
              <ShimmerLoader className="h-4 w-24 rounded" />
            ) : (
              <span className="text-sm font-semibold text-black block leading-tight truncate">
                {getDisplayName()}
              </span>
            )}
          </div>

          {/* Chevron */}
          <ChevronUp
            size={15}
            className={`flex-shrink-0 text-white/70 transition-transform duration-200 ${
              isComponentVisible ? "" : "rotate-180"
            }`}
          />
        </button>

        {/* Dropdown menu — opens upward */}
        {isComponentVisible && (
          <div className="absolute bottom-full left-0 mb-2 w-full bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-[100]">
            <button
              onClick={() => {
                toggleChild();
                setShowProfileModal(true);
              }}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 border-b border-gray-100"
            >
              <UserCircle size={16} className="text-gray-500 flex-shrink-0" />
              <span>Profile</span>
            </button>

            <button
              onClick={() => {
                toggleChild();
                openPlanModal();
              }}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700 border-b border-gray-100"
            >
              <CreditCard size={16} className="text-pink-500 flex-shrink-0" />
              <span className="text-pink-500">Upgrade Plan</span>
            </button>

            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full px-4 py-3 hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
            >
              <EnterIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Plan selection modal — Upgrade Plan */}
      <Modal isOpen={isPlanOpen} closeModal={closePlanModal}>
        <PlanDetails
          loading={planLoading}
          products={stripeProducts}
          userUsageData={usageData}
          subscriptionData={subscriptionData}
          handleClickGetStarted={handleCheckout}
        />
      </Modal>

      {/* Profile modal — portal renders outside transformed sidebar */}
      {showProfileModal && ReactDOM.createPortal(
        <ProfileModal onClose={() => setShowProfileModal(false)} />,
        document.body
      )}
    </>
  );
};

export default ProfileDropdown;
